import {
	PUBLIC_ENVIRONMENT,
	PUBLIC_SENTRY_SAMPLE_RATE
} from '$env/static/public';
import * as SentrySvelte from '@sentry/sveltekit';

export type SentryError = Error & {
	errorCode: number;
};

export type SentryEvent = {
	functionName: string;
	componentId: number;
	// ...other properties
};

export type HandleClientError = (params: {
	error: SentryError;
	event?: SentryEvent;
}) => {
	message: string;
};

export type HandleServerError = (params: {
	error: SentryError;
	event?: SentryEvent;
}) => {
	message: string;
};

/**
 * NOTE: context can record object/array data, but is depth-limited.
 * Adjust the "normalizeDepth" flag if things are being truncated too much.
 */
type SentryContext = Record<string, Record<string, unknown>>;

type ReportParams = {
	error?: unknown;
	message?: string;
	contexts?: SentryContext;
};

/**
 * Utility function for reporting errors and/or messages to sentry.
 *
 * @param args
 * @returns
 */
export function reportToSentry(args: ReportParams) {
	try {
		// For local dev the console log is sufficient
		if (PUBLIC_ENVIRONMENT === 'local') {
			console.error(JSON.stringify(args));
			return;
		}

		if (!args.error && args.message) {
			// just report a message
			SentrySvelte.captureMessage(args.message, (scope) => {
				if (args.contexts) {
					Object.entries(args.contexts).forEach(([contextName, data]) => {
						scope.setContext(contextName, data);
					});
				}

				return scope;
			});
		} else {
			SentrySvelte.captureException(args.error, (scope) => {
				if (args.contexts) {
					Object.entries(args.contexts).forEach(([contextName, data]) => {
						scope.setContext(contextName, data);
					});
				}

				if (args.message) {
					scope.setContext('message', { message: args.message });
				}

				return scope;
			});
		}
	} catch (e) {
		// Best effort to report to sentry failed all we can do is log to console
		console.log(e);
	}
}

export function getSentrySampleRate() {
	const sampleRate = Number(PUBLIC_SENTRY_SAMPLE_RATE);
	return isNaN(sampleRate) ? 1.0 : sampleRate;
}
